import { useState, useCallback, useEffect } from 'react'
import { z } from 'zod'
import { OptionType } from '@components/MultiSelectAutocomplete'
import useForm from '@hooks/useForm'
import useSession from '@hooks/useSession'
import { useLocation } from 'wouter'
import API from '@enums/api'

export interface OrgCollectionFormValues {
    name: string
    orgIds: OptionType[]
    orgAdminIds: OptionType[]
    orgCollectionAdminIds: OptionType[]
}

export interface UseOrgCollectionFormProps {
    initialValues?: OrgCollectionFormValues
    onSubmit?: (values: {
        name: string
        orgIds: string[]
        orgAdminIds: string[]
        orgCollectionAdminIds: string[]
    }) => Promise<Response>
}

const defaultValues: OrgCollectionFormValues = {
    name: '',
    orgIds: [],
    orgAdminIds: [],
    orgCollectionAdminIds: [],
}

const orgCollectionSchema = z.object({
    name: z.string().min(1, 'Please provide a name for the Org Collection.'),
    orgIds: z
        .array(z.string())
        .min(1, 'Please select at least one organization.'),
    orgAdminIds: z
        .array(z.string())
        .min(1, 'Please select at least one org admin.'),
    orgCollectionAdminIds: z
        .array(z.string())
        .min(1, 'Please select at least one collection admin.'),
})

export const formatAdminOption = (user: {
    id: string
    name?: string
    email?: string
}) => {
    return {
        id: user.id,
        label: user.name || user.email!,
    }
}

export function useOrgCollectionForm({
    initialValues,
    onSubmit,
}: UseOrgCollectionFormProps = {}) {
    const session = useSession()!
    const { values, setValue, setValues, resetForm } =
        useForm<OrgCollectionFormValues>(initialValues || defaultValues)

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [validationErrors, setValidationErrors] = useState<string[] | null>(
        null
    )
    const [success, setSuccess] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [orgs, setOrgs] = useState<OptionType[]>([])
    const [admins, setAdmins] = useState<OptionType[]>([])

    const [, setLocation] = useLocation()

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const res = await API.searches.listAllOrgs()

                if (!res.ok) {
                    throw new Error('Failed to fetch organizations')
                }

                const data = res.value
                setOrgs(data)
            } catch (err) {
                setError('Failed to fetch organizations')
                console.error('Error fetching orgs:', err)
            }
        }
        fetchOrgs()
    }, [session.accessToken])

    useEffect(() => {
        const fetchAdmins = async () => {
            try {
                const res = await API.searches.listAllAdmins()

                if (!res.ok) {
                    throw new Error('Failed to fetch admins')
                }

                const data = res.value

                const formattedAdmins = data.map((admin) => {
                    return formatAdminOption(admin)
                })

                setAdmins(formattedAdmins)
            } catch (err) {
                setError('Failed to fetch admins')
                console.error('Error fetching admins:', err)
            }
        }
        fetchAdmins()
    }, [session.accessToken])

    useEffect(() => {
        const t = setTimeout(() => validateForm(), 300)
        return () => clearTimeout(t)
    }, [values])

    const validateForm = useCallback(() => {
        const payload = {
            name: values.name,
            orgIds: values.orgIds.map((o) => String(o.id)),
            orgAdminIds: values.orgAdminIds.map((o) => String(o.id)),
            orgCollectionAdminIds: values.orgCollectionAdminIds.map((o) =>
                String(o.id)
            ),
        }

        const parseResult = orgCollectionSchema.safeParse(payload)
        if (parseResult.success) {
            setFormValid(true)
            return true
        } else {
            setFormValid(false)
            return false
        }
    }, [values])

    const formatValuesForPostRequest = (values: OrgCollectionFormValues) => {
        return {
            name: values.name,
            orgIds: values.orgIds.map((org) => String(org.id)),
            orgAdminIds: values.orgAdminIds.map((admin) => String(admin.id)),
            orgCollectionAdminIds: values.orgCollectionAdminIds.map((admin) =>
                String(admin.id)
            ),
        }
    }

    const handleSubmit = useCallback(async () => {
        const isValid = validateForm()
        if (!isValid) return

        setLoading(true)
        setError(null)
        try {
            if (onSubmit) {
                const submitResp = await onSubmit(
                    formatValuesForPostRequest(values)
                )

                const jsonResp = await submitResp.json()

                if (jsonResp.error && jsonResp.validationErrors) {
                    setValidationErrors(jsonResp.validationErrors)
                    setSuccess(false)
                    setLoading(false)
                    return
                } else if (jsonResp.error) {
                    setError(jsonResp.result)
                    setSuccess(false)
                    setLoading(false)
                    return
                }
            }
            setSuccess(true)
            setSuccess(true)
            setValidationErrors(null)
            setError(null)
            resetForm()

            setLocation('/org-collections')
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        } finally {
            setLoading(false)
        }
    }, [formValid, values, onSubmit, resetForm, validateForm])

    return {
        values,
        setValue,
        setValues,
        resetForm,
        handleSubmit,
        formValid,
        loading,
        error,
        validationErrors,
        success,
        orgs,
        admins,
    }
}
